import { Trans, useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router-dom';
import Loader from 'components/loader/Loader';
import useFeatures from 'hooks/useFeatures';
import useFetchAgency from 'components/domain/agency/useFetchAgency';
import useAppUser from 'hooks/useAppUser';
import { Alert } from 'react-bootstrap';
import useFetchFeaturedChannels from './useFetchFeaturedChannels';
import useFetchOthersChannels from './useFetchOthersChannels';
import { PropertySettingsChannelsTabWrapper } from './PropertySettingsChannelsTab.styles';
import ChannelListItem from './ChannelListItem/ChannelListItem';

const PropertySettingsChannelsTab = () => {
  const { t } = useTranslation();
  const { agencyUid } = useAppUser();
  const { allowChannelsV3 } = useFeatures();
  const {
    data: agencyData,
    isLoading: isLoadingAgency,
    isError: isErrorAgency,
  } = useFetchAgency({
    agencyUid: agencyUid as string,
  });
  const {
    featuredChannels,
    isEmpty: isEmptyFeatured,
    isLoading: isLoadingFeatured,
  } = useFetchFeaturedChannels();
  const {
    otherChannels,
    isEmpty: isEmptyOthers,
    isLoading: isLoadingOthers,
  } = useFetchOthersChannels();

  const isAgencyConfirmed = agencyData?.agencyConfirmed ?? false;

  const isEmpty = isEmptyFeatured && isEmptyOthers;
  const isLoading = isLoadingFeatured || isLoadingOthers || isLoadingAgency;
  const shouldDisplayTitles =
    !!featuredChannels?.length && !!otherChannels?.length;

  if (!allowChannelsV3) {
    return <Navigate to="/dashboard" />;
  }

  if (isLoading) {
    return <Loader />;
  }

  if (isErrorAgency) {
    return (
      <Alert bsStyle="danger" data-testid="agency-confirmed-error">
        {t('pageProperty.channels.agencyConfirmedError')}
      </Alert>
    );
  }

  if (!isAgencyConfirmed) {
    return (
      <div
        className="bd-callout bd-callout-danger"
        data-testid="agency-confirmed-warning"
      >
        <b>
          <Trans
            i18nKey="pageProperty.channels.agencyConfirmedWarning"
            components={{ 1: <Link to="/channels" /> }}
          />
        </b>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div data-testid="no-channels-message">
        <Trans
          i18nKey="pageProperty.channels.noChannels"
          components={{ 1: <Link to="/channels" /> }}
        />
      </div>
    );
  }

  return (
    <PropertySettingsChannelsTabWrapper data-testid="property-settings-channels-tab">
      {shouldDisplayTitles && (
        <h2 data-testid="popular-channels-title">
          {t('pageProperty.channels.popularChannelsTitle')}
        </h2>
      )}
      <ul data-testid="featured-channels-list">
        {featuredChannels?.map((channel) => (
          <ChannelListItem key={channel.id} channelIntegration={channel} />
        ))}
      </ul>

      {shouldDisplayTitles && (
        <h2 data-testid="others-channels-title">
          {t('pageProperty.channels.othersChannelsTitle')}
        </h2>
      )}
      <ul data-testid="others-channels-list">
        {otherChannels?.map((channel) => (
          <ChannelListItem key={channel.id} channelIntegration={channel} />
        ))}
      </ul>
    </PropertySettingsChannelsTabWrapper>
  );
};

export default PropertySettingsChannelsTab;
